import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Badge,
  Box,
  CircularProgress,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  NotificationsNone as NotificationsNoneIcon,
} from '@mui/icons-material';

import MessageSnackbar from '@/__core/helpers/components/MessageSnackbar';
import { formatVietnameseDateTime } from '@/__core/helpers/utils';
import type { RootState } from '@/setup/redux/RootReducer';
import { TaskNotification } from '@/modules/tasks/models/TaskModel';
import {
  fetchNotifications,
  fetchUnreadCount,
  markRead,
  startNotificationSocket,
  stopNotificationSocket,
} from '@/modules/tasks/redux/TaskNotificationRedux';

const COMMENT_TYPES = ['comment_created', 'comment_read'];

const isCommentNotification = (notification: TaskNotification): boolean =>
  COMMENT_TYPES.includes(notification.type);

export default function TaskNotificationMenu() {
  const dispatch = useDispatch();

  const items = useSelector((state: RootState) => state.taskNotifications.items);
  const unreadCount = useSelector(
    (state: RootState) => state.taskNotifications.unreadCount
  );
  const loading = useSelector((state: RootState) => state.taskNotifications.loading);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const open = Boolean(anchorEl);

  const unreadItems = useMemo(() => items.filter((item) => !item.is_read), [items]);
  const taskItems = useMemo(
    () => unreadItems.filter((item) => !isCommentNotification(item)),
    [unreadItems]
  );
  const commentItems = useMemo(
    () => unreadItems.filter(isCommentNotification),
    [unreadItems]
  );

  useEffect(() => {
    dispatch(fetchUnreadCount());
    dispatch(fetchNotifications({ is_read: false }));
    dispatch(startNotificationSocket());

    return () => {
      dispatch(stopNotificationSocket());
    };
  }, [dispatch]);

  const handleOpen = () => {
    setAnchorEl(buttonRef.current);
    dispatch(fetchNotifications({ is_read: false }));
    dispatch(fetchUnreadCount());
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = useCallback(
    async (notification: TaskNotification) => {
      try {
        await (dispatch(markRead(notification.id)) as any).unwrap();
      } catch {
        setError('Không thể đánh dấu thông báo đã đọc');
      }
    },
    [dispatch]
  );

  const renderItem = (notification: TaskNotification) => (
    <Box
      key={notification.id}
      role='button'
      tabIndex={0}
      onClick={() => {
        void handleItemClick(notification);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          void handleItemClick(notification);
        }
      }}
      sx={{
        px: 2,
        py: 1.25,
        cursor: 'pointer',
        borderLeft: '3px solid #2563eb',
        '&:hover': { backgroundColor: '#f8fafc' },
        '&:focus-visible': { outline: '2px solid #2563eb', outlineOffset: -2 },
      }}
    >
      <Typography sx={{ fontSize: '0.825rem', color: '#1e293b', lineHeight: 1.4 }}>
        {notification.message || 'Thông báo mới'}
      </Typography>
      <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', mt: 0.25 }}>
        {formatVietnameseDateTime(notification.created_at)}
      </Typography>
    </Box>
  );

  const renderGroup = (label: string, list: TaskNotification[]) => {
    if (list.length === 0) {
      return null;
    }
    return (
      <Box key={label}>
        <Box
          sx={{
            px: 2,
            pt: 1.25,
            pb: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 3,
              height: 12,
              borderRadius: '2px',
              backgroundColor: '#2563eb',
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              color: '#334155',
            }}
          >
            {label}
          </Typography>
        </Box>
        {list.map(renderItem)}
      </Box>
    );
  };

  const hasUnreadItems = taskItems.length > 0 || commentItems.length > 0;

  return (
    <>
      <Tooltip title='Thông báo'>
        <IconButton
          ref={buttonRef}
          onClick={handleOpen}
          aria-label='Thông báo'
          aria-haspopup='true'
          aria-expanded={open ? 'true' : undefined}
          sx={{
            p: 0.5,
            mr: 0.5,
            width: 32,
            height: 32,
            color: '#475569',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          <Badge
            color='error'
            overlap='rectangular'
            badgeContent={unreadCount > 0 ? unreadCount : undefined}
            max={99}
          >
            <NotificationsNoneIcon sx={{ fontSize: 24 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableRestoreFocus
        disableScrollLock
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 340,
              maxWidth: 420,
              maxHeight: 460,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
              boxShadow:
                '0 8px 24px rgba(15,23,42,0.10), 0 2px 6px rgba(15,23,42,0.06)',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.95rem',
              fontWeight: 700,
              color: '#fff',
            }}
          >
            Thông báo
          </Typography>
          <Typography
            sx={{
              fontSize: '0.7rem',
              color: 'rgba(255,255,255,0.75)',
              mt: 0.25,
            }}
          >
            {unreadCount > 0 ? `${unreadCount} chưa đọc` : 'Đã đọc hết'}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
          {loading && !hasUnreadItems ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                py: 3,
              }}
            >
              <CircularProgress size={16} thickness={4} sx={{ color: '#2563eb' }} />
              <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                Đang tải...
              </Typography>
            </Box>
          ) : hasUnreadItems ? (
            <>
              {renderGroup('Task', taskItems)}
              {renderGroup('Bình luận', commentItems)}
            </>
          ) : (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.825rem', color: '#94a3b8' }}>
                Không có thông báo mới
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>

      <MessageSnackbar
        open={Boolean(error)}
        message={error}
        severity='error'
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </>
  );
}
